import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Search, Star, Sparkles, Loader2, Plus, ThumbsUp, Meh, ThumbsDown, Check, Trophy, Mic, Headphones, Calendar, ListOrdered, GripVertical, List, ArrowUp, ArrowDown, ChevronLeft, ChevronRight, Share2, Send, MessageSquare, X } from 'lucide-react';
import { toast } from 'sonner';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { AppHeader } from '../components/AppHeader';
import { MediaCard } from '../components/MediaCard';
import { AddToListDialog } from '../components/AddToListDialog';
import { searchMedia, discoverMedia, getGenres, toListenNotesFormat, getPodcastDetails, MediaType } from '../services/mediaService';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const grades = [
  { g: 'liked', icon: <ThumbsUp className="w-6 h-6 text-green-500" />, label: 'Liked it', cls: 'border-green-200 hover:border-green-400 hover:bg-green-50' },
  { g: 'alright', icon: <Meh className="w-6 h-6 text-amber-500" />, label: 'It was alright', cls: 'border-amber-200 hover:border-amber-400 hover:bg-amber-50' },
  { g: 'disliked', icon: <ThumbsDown className="w-6 h-6 text-red-400" />, label: "Didn't like it", cls: 'border-red-200 hover:border-red-400 hover:bg-red-50' }
];

const getGradeLabel = (g) => ({ liked: 'liked', alright: 'alright', disliked: 'disliked' }[g] || g);

// Sortable Ranking Item for the ranking dialog
const SortableRankingItem = ({ podcast, isHighlighted }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: podcast.podcast_id });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all ${
        isHighlighted 
          ? 'border-[#F87171] bg-[#F87171]/5 shadow-md' 
          : 'border-slate-200 bg-white hover:border-slate-300'
      }`}
    >
      <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing">
        <GripVertical className={`w-5 h-5 ${isHighlighted ? 'text-[#F87171]' : 'text-slate-300'}`} />
      </div>
      
      <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-slate-100">
        {podcast.image ? (
          <img src={podcast.image} alt={podcast.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Mic className="w-5 h-5 text-slate-300" />
          </div>
        )}
      </div>
      
      <div className="flex-1 min-w-0">
        <h3 className={`font-medium truncate ${isHighlighted ? 'text-[#F87171]' : 'text-[#3D4F6F]'}`}>
          {podcast.title}
        </h3>
        <p className="text-xs text-slate-500 truncate">{podcast.publisher}</p>
      </div>
      
      {isHighlighted && (
        <span className="px-2 py-1 bg-[#F87171] text-white text-xs rounded-full font-medium">New</span>
      )}
    </div>
  );
};

export default function PodcastDiscoverPage({ user }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [genres, setGenres] = useState([]);
  const [selectedGenre, setSelectedGenre] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [mode, setMode] = useState('discover');
  const [selectedPodcast, setSelectedPodcast] = useState(null);
  const [podcastDetails, setPodcastDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  
  // User's watchlists
  const [watchlists, setWatchlists] = useState([]);
  
  // Add to List state
  const [addToListItem, setAddToListItem] = useState(null);
  const [selectedListId, setSelectedListId] = useState(null);
  const [addingToList, setAddingToList] = useState(false);
  
  // Add & Rank dialog state
  const [isRankDialogOpen, setIsRankDialogOpen] = useState(false);
  const [newlyAddedPodcast, setNewlyAddedPodcast] = useState(null);
  const [rankingOrder, setRankingOrder] = useState([]);
  const [rankingListId, setRankingListId] = useState(null); // Store the list ID for ranking
  
  // DnD sensors for ranking
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  
  // Mark as Listened state (matching Films workflow exactly)
  const [listenedItem, setListenedItem] = useState(null);
  const [selectedGrade, setSelectedGrade] = useState(null);
  const [savingListened, setSavingListened] = useState(false);
  const [listenedComment, setListenedComment] = useState('');
  const [isFirstListen, setIsFirstListen] = useState(true);
  const [listenDate, setListenDate] = useState(new Date().toISOString().split('T')[0]);
  const [primaryGenre, setPrimaryGenre] = useState(null);
  const [customGenre, setCustomGenre] = useState('');
  
  // Share with friend state
  const [showSharePanel, setShowSharePanel] = useState(false);
  const [friends, setFriends] = useState([]);
  const [loadingFriends, setLoadingFriends] = useState(false);
  const [sharingWith, setSharingWith] = useState(null);
  const [shareMessage, setShareMessage] = useState('');
  
  // Comparison ranking state
  const [comparisonStep, setComparisonStep] = useState(false);
  const [existingItems, setExistingItems] = useState([]);
  const [comparisonItem, setComparisonItem] = useState(null);
  const [searchLow, setSearchLow] = useState(0);
  const [searchHigh, setSearchHigh] = useState(0);

  useEffect(() => {
    fetchGenres();
    fetchWatchlists();
    fetchDiscover();
    
    // Check if we should show a specific item (from social feed click)
    const itemParam = searchParams.get('item');
    if (itemParam) {
      // Fetch and display the podcast
      handleViewDetails({ podcast_id: itemParam });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (mode === 'discover') {
      fetchDiscover();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGenre, page, mode]);

  const fetchGenres = async () => {
    try {
      // Use canonical media API for genres
      const genres = await getGenres(MediaType.PODCAST);
      // Genres come back as {id, name} objects - keep them in that format for the select
      const topLevel = (genres || []).filter(g => !g.parent_id);
      setGenres(topLevel);
    } catch (error) {
      console.error('Canonical genres failed:', error);
      // Fallback to old API
      try {
        const response = await axios.get(`${API}/podcasts/genres`, { withCredentials: true });
        const topLevel = (response.data.genres || []).filter(g => !g.parent_id);
        setGenres(topLevel);
      } catch {
        // eslint-disable-next-line no-empty -- Intentional: Genres are non-critical UI enhancement, silent fail is acceptable
      }
    }
  };

  const fetchWatchlists = async () => {
    try {
      const response = await axios.get(`${API}/podcast-lists`, { withCredentials: true });
      setWatchlists(response.data || []);
    } catch {
      // eslint-disable-next-line no-empty -- Intentional: Watchlists dropdown is non-critical, silent fail is acceptable
    }
  };

  const fetchDiscover = async () => {
    setLoading(true);
    try {
      // Try canonical media API first
      const genreName = selectedGenre !== 'all' 
        ? genres.find(g => g.id.toString() === selectedGenre)?.name 
        : null;
      
      const result = await discoverMedia(MediaType.PODCAST, {
        page,
        limit: 24,
        genre: genreName
      });
      
      // Convert canonical items to Listen Notes format for compatibility
      const podcasts = result.items.map(item => {
        const lnFormat = toListenNotesFormat(item);
        return {
          podcast_id: lnFormat.listen_notes_id || item.id,
          id: lnFormat.listen_notes_id || item.id,
          title: lnFormat.title,
          publisher: lnFormat.publisher,
          description: lnFormat.description,
          image: lnFormat.image,
          thumbnail: lnFormat.thumbnail,
          total_episodes: lnFormat.total_episodes,
          genres: lnFormat.genres,
          listen_score: lnFormat.listen_score,
          language: lnFormat.language,
          country: lnFormat.country,
          website: lnFormat.website,
          _canonical: item
        };
      });
      
      setResults(podcasts);
      setTotalPages(result.totalPages || 1);
      setMode('discover');
    } catch (error) {
      console.error('Canonical API failed, falling back:', error);
      // Fallback to old API
      try {
        const params = new URLSearchParams({ page: page.toString() });
        if (selectedGenre !== 'all') {
          params.append('genre_id', selectedGenre);
        }
        const response = await axios.get(`${API}/podcasts/discover?${params}`, { withCredentials: true });
        setResults(response.data.results || []);
        setTotalPages(response.data.total_pages || 1);
        setMode('discover');
      } catch (e) {
        toast.error('Failed to load podcasts');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!searchQuery.trim()) {
      setMode('discover');
      fetchDiscover();
      return;
    }
    
    setLoading(true);
    setMode('search');
    try {
      // Try canonical media API first
      const result = await searchMedia(searchQuery, MediaType.PODCAST, page, 24);
      
      // Convert canonical items to Listen Notes format for compatibility
      const podcasts = result.items.map(item => {
        const lnFormat = toListenNotesFormat(item);
        return {
          podcast_id: lnFormat.listen_notes_id || item.id,
          id: lnFormat.listen_notes_id || item.id,
          title: lnFormat.title,
          publisher: lnFormat.publisher,
          description: lnFormat.description,
          image: lnFormat.image,
          thumbnail: lnFormat.thumbnail,
          total_episodes: lnFormat.total_episodes,
          genres: lnFormat.genres,
          listen_score: lnFormat.listen_score,
          language: lnFormat.language,
          country: lnFormat.country,
          website: lnFormat.website,
          _canonical: item
        };
      });
      
      setResults(podcasts);
      setTotalPages(Math.min(result.totalPages || 1, 100));
    } catch (error) {
      console.error('Canonical search failed, falling back:', error);
      // Fallback to old API
      try {
        const response = await axios.get(`${API}/podcasts/search?query=${encodeURIComponent(searchQuery)}&page=${page}`, { withCredentials: true });
        setResults(response.data.results || []);
        setTotalPages(Math.min(response.data.total_pages || 1, 100));
      } catch (e) {
        toast.error('Search failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setMode('discover');
    setPage(1);
    fetchDiscover();
  };

  const handleViewDetails = async (podcast) => {
    setSelectedPodcast(podcast);
    setLoadingDetails(true);
    try {
      // Use canonical media API for podcast details
      const podcastId = podcast.podcast_id || podcast.id;
      const details = await getPodcastDetails(podcastId);
      setPodcastDetails(details);
    } catch (error) {
      console.error('Canonical podcast details failed, falling back:', error);
      // Fallback to old API
      try {
        const response = await axios.get(`${API}/podcasts/${podcast.podcast_id}`, { withCredentials: true });
        setPodcastDetails(response.data);
      } catch (e) {
        toast.error('Failed to load podcast details');
        setSelectedPodcast(null);
      }
    } finally {
      setLoadingDetails(false);
    }
  };

  // Add to List handlers
  const handleOpenAddToList = (podcast, e) => {
    e?.stopPropagation();
    setAddToListItem(podcast);
    setSelectedListId(null);
  };

  // Handler for AddToListDialog component - receives (listId, item, position)
  const handleAddToListFromDialog = async (listId, item, position) => {
    setSelectedListId(listId);
    setAddingToList(true);
    try {
      await axios.post(`${API}/podcast-lists/${listId}/podcasts`, {
        podcast_id: item.podcast_id || item.id,
        title: item.title,
        publisher: item.publisher,
        image: item.image,
        thumbnail: item.thumbnail,
        description: item.description,
        total_episodes: item.total_episodes,
        genres: item.genres || [],
        position: position
      }, { withCredentials: true });
      const positionText = position === 'top' ? 'at #1' : 'at the bottom';
      toast.success(`Added "${item.title}" ${positionText}!`);
      setAddToListItem(null);
      setSelectedListId(null);
      fetchWatchlists();
    } catch (error) {
      if (error.response?.data?.detail === 'Already in list') {
        toast.error('Already in this list');
      } else {
        toast.error('Failed to add to list');
      }
    } finally {
      setAddingToList(false);
    }
  };

  // Handler for AddToListDialog "Add & Rank" - receives (list, item)
  const handleAddAndRankFromDialog = async (list, item) => {
    setSelectedListId(list.id);
    setAddingToList(true);
    try {
      const response = await axios.post(`${API}/podcast-lists/${list.id}/podcasts`, {
        podcast_id: item.podcast_id || item.id,
        title: item.title,
        publisher: item.publisher,
        image: item.image,
        thumbnail: item.thumbnail,
        description: item.description,
        total_episodes: item.total_episodes,
        genres: item.genres || [],
        position: 'bottom'
      }, { withCredentials: true });
      
      if (response.data.podcasts && response.data.podcasts.length > 1) {
        setNewlyAddedPodcast(item);
        setRankingOrder([...response.data.podcasts]);
        setRankingListId(list.id);
        setAddToListItem(null);
        setSelectedListId(null);
        setIsRankDialogOpen(true);
      } else {
        toast.success(`Added "${item.title}"!`);
        setAddToListItem(null);
        setSelectedListId(null);
      }
      fetchWatchlists();
    } catch (error) {
      if (error.response?.data?.detail === 'Already in list') {
        toast.error('Already in this list');
      } else {
        toast.error('Failed to add to list');
      }
    } finally {
      setAddingToList(false);
    }
  };

  // Legacy handlers (kept for backward compatibility)
  const handleAddToList = async (position) => {
    if (!selectedListId || !addToListItem) return;
    await handleAddToListFromDialog(selectedListId, addToListItem, position);
  };

  const handleAddAndRank = async () => {
    if (!selectedListId || !addToListItem) return;
    const list = watchlists.find(l => l.id === selectedListId);
    if (list) await handleAddAndRankFromDialog(list, addToListItem);
  };

  const handleRankingDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      const oldIndex = rankingOrder.findIndex(p => p.podcast_id === active.id);
      const newIndex = rankingOrder.findIndex(p => p.podcast_id === over.id);
      setRankingOrder(arrayMove(rankingOrder, oldIndex, newIndex));
    }
  };

  const handleSaveRankingOrder = async () => {
    if (!rankingListId) {
      toast.error('Could not find the list');
      setIsRankDialogOpen(false);
      setNewlyAddedPodcast(null);
      setRankingListId(null);
      return;
    }
    
    try {
      await axios.put(`${API}/podcast-lists/${rankingListId}/reorder`, {
        podcast_order: rankingOrder.map(p => p.podcast_id)
      }, { withCredentials: true });
      
      toast.success(`"${newlyAddedPodcast?.title}" added and ranked!`);
      setIsRankDialogOpen(false);
      setNewlyAddedPodcast(null);
      setRankingListId(null);
      fetchWatchlists();
    } catch (error) {
      toast.error('Failed to save order');
    }
  };

  // Listened handlers (matching Films workflow)
  const handleOpenListened = (podcast, e) => {
    e?.stopPropagation();
    setListenedItem(podcast);
    setSelectedGrade(null);
    setComparisonStep(false);
    setListenedComment('');
    setIsFirstListen(true);
    setListenDate(new Date().toISOString().split('T')[0]);
    setPrimaryGenre(podcast?.genres?.[0] || null);
    setCustomGenre('');
  };

  const resetListenedDialog = () => {
    setListenedItem(null);
    setSelectedGrade(null);
    setComparisonStep(false);
    setListenedComment('');
    setIsFirstListen(true);
    setListenDate(new Date().toISOString().split('T')[0]);
    setExistingItems([]);
    setComparisonItem(null);
    setPrimaryGenre(null);
    setCustomGenre('');
  };

  // Fetch friends for sharing
  const fetchFriends = async () => {
    setLoadingFriends(true);
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/messages/friends`, { withCredentials: true, headers });
      setFriends(response.data.friends || []);
    } catch (error) {
      console.error('Failed to fetch friends:', error);
    } finally {
      setLoadingFriends(false);
    }
  };

  // Handle sharing podcast with a friend
  const handleShareWithFriend = async (friendId, podcast) => {
    setSharingWith(friendId);
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      await axios.post(`${API}/messages`, {
        recipient_id: friendId,
        content: shareMessage || `Check out ${podcast.title}!`,
        message_type: 'media_share',
        media_data: {
          media_type: 'podcast',
          item_id: podcast.podcast_id || podcast.id,
          title: podcast.title,
          poster: podcast.image,
          year: null
        }
      }, { withCredentials: true, headers });
      
      toast.success('Shared with friend!');
      setShowSharePanel(false);
      setShareMessage('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to share');
    } finally {
      setSharingWith(null);
    }
  };

  // Open share panel
  const openSharePanel = () => {
    setShowSharePanel(true);
    fetchFriends();
  };

  const handleGradeSelect = (grade) => {
    setSelectedGrade(grade);
  };

  const handleContinueToRanking = async () => {
    if (!selectedGrade) return;
    setSavingListened(true);
    
    try {
      const response = await axios.get(`${API}/listened-podcasts?grade=${selectedGrade}`, { withCredentials: true });
      const items = response.data.podcasts || [];
      setExistingItems(items);
      
      if (items.length === 0) {
        // No items to compare, save directly
        await saveListened(selectedGrade, 1);
      } else {
        // Start comparison
        setSearchLow(0);
        setSearchHigh(items.length);
        const midIndex = Math.floor(items.length / 2);
        setComparisonItem(items[midIndex]);
        setComparisonStep(true);
        setSavingListened(false);
      }
    } catch (error) {
      toast.error('Failed to fetch existing items');
      setSavingListened(false);
    }
  };

  const handleComparisonChoice = async (preferNew) => {
    let newLow = searchLow;
    let newHigh = searchHigh;
    
    const currentIndex = existingItems.findIndex(i => i.id === comparisonItem.id);
    
    if (preferNew) {
      newHigh = currentIndex;
    } else {
      newLow = currentIndex + 1;
    }
    
    setSearchLow(newLow);
    setSearchHigh(newHigh);
    
    if (newLow >= newHigh) {
      // Found position, save
      await saveListened(selectedGrade, newLow + 1);
    } else {
      // Continue searching
      const midIndex = Math.floor((newLow + newHigh) / 2);
      setComparisonItem(existingItems[midIndex]);
    }
  };

  const saveListened = async (grade, rank) => {
    setSavingListened(true);
    try {
      await axios.post(`${API}/listened-podcasts/direct`, {
        podcast_id: listenedItem.podcast_id,
        title: listenedItem.title,
        publisher: listenedItem.publisher,
        image: listenedItem.image,
        thumbnail: listenedItem.thumbnail,
        description: listenedItem.description,
        total_episodes: listenedItem.total_episodes,
        genres: listenedItem.genres || [],
        primary_genre: customGenre || primaryGenre,
        grade: grade,
        rank_in_group: rank,
        comment: listenedComment || null,
        is_first_listen: isFirstListen,
        listen_date: listenDate || null
      }, { withCredentials: true });
      
      toast.success(`Ranked #${rank} in your ${getGradeLabel(grade)} podcasts!`);
      resetListenedDialog();
    } catch (error) {
      if (error.response?.data?.detail === 'Already marked as listened') {
        toast.error('Already in your listened list');
      } else {
        toast.error('Failed to save');
      }
    } finally {
      setSavingListened(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#3D4F6F]">
      {/* Header with full navigation */}
      <AppHeader user={user} showBack={true} backPath="/podcasts" />

      <main className="container mx-auto px-6 py-8">
        {/* Page title */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-white/90 rounded-lg flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-[#F87171]" />
          </div>
          <div>
            <h1 className="font-outfit text-xl sm:text-2xl font-bold text-white">Discover Podcasts</h1>
            <p className="text-white/70 text-sm">Find your next favorite podcast</p>
          </div>
        </div>

        {/* Search and Filters */}
        <Card className="border-0 shadow-xl bg-white/95 backdrop-blur-sm mb-6">
          <CardContent className="p-4 space-y-4">
            <form onSubmit={handleSearch} className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input placeholder="Search podcasts..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" data-testid="podcast-search-input" />
              </div>
              <Button type="submit" className="bg-[#F87171] hover:bg-[#EF4444] text-white" data-testid="podcast-search-btn">Search</Button>
              {mode === 'search' && <Button type="button" variant="outline" onClick={clearSearch}>Clear</Button>}
            </form>

            {mode === 'discover' && (
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-[#64748B]">Genre:</span>
                  <Select value={selectedGenre} onValueChange={(val) => { setSelectedGenre(val); setPage(1); }}>
                    <SelectTrigger className="w-[200px]" data-testid="podcast-genre-filter">
                      <SelectValue placeholder="All Genres" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Genres</SelectItem>
                      {genres.map((genre) => (
                        <SelectItem key={genre.id} value={genre.id.toString()}>{genre.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Results */}
        <Card className="border-0 shadow-xl bg-white/95 backdrop-blur-sm">
          <CardContent className="p-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#F87171]" />
              </div>
            ) : results.length === 0 ? (
              <div className="text-center py-12">
                <Mic className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p className="text-slate-500">No podcasts found</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {results.slice(0, 24).map((podcast) => (
                    <MediaCard 
                      key={podcast.podcast_id || podcast.id} 
                      item={{...podcast, tmdb_id: podcast.podcast_id || podcast.id}}
                      mediaType="podcast"
                      onClick={() => handleViewDetails(podcast)}
                      onAddToList={() => handleOpenAddToList(podcast)}
                      onMarkWatched={() => handleOpenListened(podcast)}
                      testIdPrefix="podcast"
                    />
                  ))}
                </div>

                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-4 mt-8">
                    <Button variant="outline" onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1}>Previous</Button>
                    <span className="text-sm text-[#3D4F6F]">Page {page} of {totalPages}</span>
                    <Button variant="outline" onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page >= totalPages}>Next</Button>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Podcast Details Dialog */}
      <Dialog open={!!selectedPodcast} onOpenChange={(open) => !open && setSelectedPodcast(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          {loadingDetails ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#F87171]" />
            </div>
          ) : podcastDetails && (
            <>
              <DialogHeader>
                <DialogTitle className="font-outfit text-[#3D4F6F] text-xl">{podcastDetails.title}</DialogTitle>
                <DialogDescription className="text-slate-500">{podcastDetails.publisher}</DialogDescription>
              </DialogHeader>
              
              <div className="grid md:grid-cols-3 gap-6 py-4">
                <div className="md:col-span-1">
                  {podcastDetails.image ? (
                    <img src={podcastDetails.image} alt={podcastDetails.title} className="w-full rounded-xl shadow-lg" />
                  ) : (
                    <div className="w-full aspect-square bg-gradient-to-br from-[#3D4F6F] to-[#F87171] rounded-xl flex items-center justify-center">
                      <Mic className="w-16 h-16 text-white/50" />
                    </div>
                  )}
                </div>
                
                <div className="md:col-span-2 space-y-4">
                  <div className="flex items-center gap-4 flex-wrap">
                    {podcastDetails.listen_score && typeof podcastDetails.listen_score === 'number' && (
                      <div className="flex items-center gap-1 bg-[#F87171]/10 px-3 py-1 rounded-full">
                        <Star className="w-4 h-4 text-[#F87171] fill-[#F87171]" />
                        <span className="font-medium text-[#3D4F6F]">{podcastDetails.listen_score}</span>
                      </div>
                    )}
                    <span className="text-sm text-slate-500">{podcastDetails.total_episodes} episodes</span>
                    {podcastDetails.language && <span className="text-sm text-slate-500">{podcastDetails.language}</span>}
                  </div>
                  
                  <p className="text-sm text-slate-600 leading-relaxed">{podcastDetails.description}</p>
                  
                  {podcastDetails.episodes?.length > 0 && (
                    <div>
                      <h4 className="font-medium text-[#3D4F6F] mb-2">Recent Episodes</h4>
                      <div className="space-y-2">
                        {podcastDetails.episodes.slice(0, 5).map((ep) => (
                          <div key={ep.id} className="p-2 bg-slate-50 rounded-lg">
                            <p className="text-sm font-medium text-[#3D4F6F] line-clamp-1">{ep.title}</p>
                            {ep.audio_length_sec && <p className="text-xs text-slate-400">{Math.round(ep.audio_length_sec / 60)} min</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <DialogFooter className="flex flex-col sm:flex-row gap-2">
                <div className="flex gap-2 flex-1">
                  <Button className="flex-1 bg-[#F87171] hover:bg-[#EF4444] text-white" onClick={() => { setSelectedPodcast(null); handleOpenAddToList(podcastDetails); }}>
                    <Plus className="w-4 h-4 mr-2" />Add to List
                  </Button>
                  <Button variant="outline" className="flex-1" onClick={() => { setSelectedPodcast(null); handleOpenListened(podcastDetails); }}>
                    <Headphones className="w-4 h-4 mr-2" />Mark Listened
                  </Button>
                  <Button variant="outline" onClick={openSharePanel} className="border-[#F87171] text-[#F87171] hover:bg-[#F87171]/10">
                    <Share2 className="w-4 h-4 mr-2" />Share
                  </Button>
                </div>
              </DialogFooter>
              
              {/* Share with Friend Panel */}
              {showSharePanel && (
                <div className="mt-3 p-3 bg-slate-50 rounded-xl border border-slate-200">
                  <div className="flex items-center gap-2 mb-3">
                    <MessageSquare className="w-4 h-4 text-[#F87171]" />
                    <span className="font-medium text-sm text-[#3D4F6F]">Share with a friend</span>
                    <button 
                      onClick={() => setShowSharePanel(false)}
                      className="ml-auto text-slate-400 hover:text-slate-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <input
                    type="text"
                    value={shareMessage}
                    onChange={(e) => setShareMessage(e.target.value)}
                    placeholder={`Check out ${podcastDetails?.title}!`}
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg mb-3 focus:outline-none focus:ring-2 focus:ring-[#F87171]/30"
                  />
                  {loadingFriends ? (
                    <div className="flex justify-center py-4">
                      <Loader2 className="w-5 h-5 animate-spin text-[#F87171]" />
                    </div>
                  ) : friends.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-2">
                      No friends yet. Follow someone and get followed back!
                    </p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {friends.slice(0, 6).map((friend) => (
                        <button
                          key={friend.user_id}
                          onClick={() => handleShareWithFriend(friend.user_id, podcastDetails)}
                          disabled={sharingWith === friend.user_id}
                          className="flex items-center gap-2 px-3 py-1.5 bg-white border border-slate-200 rounded-full hover:border-[#F87171] hover:bg-[#F87171]/5 transition-colors text-sm disabled:opacity-50"
                        >
                          {sharingWith === friend.user_id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <Avatar className="h-5 w-5">
                                {friend.avatar_url ? (
                                  <AvatarImage src={friend.avatar_url} />
                                ) : (
                                  <AvatarFallback className="bg-[#F87171]/10 text-[#F87171] text-xs">
                                    {friend.name?.charAt(0)}
                                  </AvatarFallback>
                                )}
                              </Avatar>
                              <span className="text-[#3D4F6F]">{friend.name?.split(' ')[0]}</span>
                              <Send className="w-3 h-3 text-[#F87171]" />
                            </>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Add to List Dialog - Using reusable component */}
      <AddToListDialog
        open={!!addToListItem}
        onClose={() => { setAddToListItem(null); setSelectedListId(null); }}
        item={addToListItem}
        watchlists={watchlists}
        onAddToList={handleAddToListFromDialog}
        onAddAndRank={handleAddAndRankFromDialog}
        onCreateList={() => { setAddToListItem(null); navigate('/podcasts'); }}
        loading={addingToList}
        mediaType="podcast"
      />

      {/* Mark as Listened Dialog (Matching Films workflow exactly) */}
      <Dialog open={!!listenedItem} onOpenChange={() => resetListenedDialog()}>
        <DialogContent className="max-w-lg" data-testid="mark-listened-dialog">
          {!comparisonStep ? (
            <>
              <DialogHeader>
                <DialogTitle className="font-outfit text-[#3D4F6F]">How was it?</DialogTitle>
                <DialogDescription className="text-[#64748B]">Rate your experience listening to &ldquo;{listenedItem?.title}&rdquo;</DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                {/* Grade Selection */}
                <div className="space-y-2">
                  <p className="text-sm font-medium text-[#64748B] mb-2">How did you like it?</p>
                  {grades.map(({ g, icon, label, cls }) => (
                    <button 
                      key={g} 
                      data-testid={`grade-${g}-btn`} 
                      onClick={() => handleGradeSelect(g)} 
                      className={`w-full flex items-center gap-4 p-3 rounded-xl border-2 transition-all ${
                        selectedGrade === g ? cls.replace('hover:', '') : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className={selectedGrade === g ? '' : 'opacity-50'}>{icon}</div>
                      <span className={`font-medium ${selectedGrade === g ? '' : 'text-slate-500'}`}>{label}</span>
                      {selectedGrade === g && <Check className="w-5 h-5 ml-auto text-current" />}
                    </button>
                  ))}
                </div>
                
                {/* First Time Listen Toggle */}
                <div className="pt-2 border-t border-slate-100">
                  <button
                    onClick={() => setIsFirstListen(!isFirstListen)}
                    className={`w-full flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all ${
                      isFirstListen ? 'border-purple-400 bg-purple-50 text-purple-700' : 'border-slate-200 hover:border-slate-300 text-[#64748B]'
                    }`}
                    data-testid="first-listen-toggle"
                  >
                    <Headphones className={`w-5 h-5 ${isFirstListen ? 'text-purple-600' : 'text-slate-400'}`} />
                    <span className="font-medium text-sm">First time listening</span>
                  </button>
                </div>
                
                {/* Listen Date */}
                <div className="pt-2">
                  <label className="text-sm font-medium text-[#64748B] mb-2 block flex items-center gap-2">
                    <Calendar className="w-4 h-4" />Date completed
                  </label>
                  <Input type="date" value={listenDate} onChange={(e) => setListenDate(e.target.value)} className="w-full" data-testid="listen-date-input" />
                </div>
                
                {/* Comments/Thoughts */}
                <div className="pt-2">
                  <label className="text-sm font-medium text-[#64748B] mb-2 block">Thoughts (optional)</label>
                  <Textarea placeholder="What did you think about it?" value={listenedComment} onChange={(e) => setListenedComment(e.target.value)} className="resize-none h-20" data-testid="listen-comment-input" />
                </div>
                
                {/* Primary Genre Selection */}
                <div className="pt-2">
                  <label className="text-sm font-medium text-[#64748B] mb-2 block">
                    Primary Genre (for stats)
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {(listenedItem?.genres || []).map((genre) => (
                      <button
                        key={genre}
                        type="button"
                        onClick={() => { setPrimaryGenre(genre); setCustomGenre(''); }}
                        className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                          primaryGenre === genre && !customGenre
                            ? 'bg-[#F87171] text-white border-[#F87171]'
                            : 'bg-white text-[#64748B] border-slate-200 hover:border-[#F87171]'
                        }`}
                        data-testid={`genre-select-${genre}`}
                      >
                        {genre}
                      </button>
                    ))}
                    {/* Custom Genre Option */}
                    <button
                      type="button"
                      onClick={() => setPrimaryGenre('custom')}
                      className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                        primaryGenre === 'custom' || customGenre
                          ? 'bg-purple-500 text-white border-purple-500'
                          : 'bg-white text-[#64748B] border-slate-200 hover:border-purple-400'
                      }`}
                      data-testid="genre-select-custom"
                    >
                      + Custom
                    </button>
                  </div>
                  {/* Custom Genre Input */}
                  {(primaryGenre === 'custom' || customGenre) && (
                    <div className="mt-2">
                      <Input
                        type="text"
                        placeholder="Enter your custom genre..."
                        value={customGenre}
                        onChange={(e) => setCustomGenre(e.target.value)}
                        className="text-sm"
                        data-testid="custom-genre-input"
                      />
                    </div>
                  )}
                </div>
                
                {/* Continue to Ranking Button */}
                <Button
                  onClick={handleContinueToRanking}
                  disabled={!selectedGrade || savingListened}
                  className="w-full bg-[#F87171] hover:bg-[#EF4444] text-white rounded-full mt-2"
                  data-testid="continue-to-ranking-btn"
                >
                  {savingListened ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <ArrowUp className="w-4 h-4 mr-2" />
                  )}
                  Continue to Ranking
                </Button>
              </div>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="font-outfit text-[#3D4F6F] flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-amber-500" />Which do you prefer?
                </DialogTitle>
                <DialogDescription className="text-[#64748B]">Comparing against your {getGradeLabel(selectedGrade)} ({existingItems.length} total)</DialogDescription>
              </DialogHeader>
              
              <div className="py-6">
                <div className="grid grid-cols-2 gap-4">
                  {/* New Item */}
                  <button
                    onClick={() => handleComparisonChoice(true)}
                    className="p-4 rounded-xl border-2 border-slate-200 hover:border-[#F87171] hover:bg-[#F87171]/5 transition-all text-left"
                    data-testid="prefer-new-btn"
                  >
                    <div className="aspect-square rounded-lg overflow-hidden bg-slate-100 mb-3">
                      {listenedItem?.image ? (
                        <img src={listenedItem.image} alt={listenedItem.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center"><Mic className="w-10 h-10 text-slate-300" /></div>
                      )}
                    </div>
                    <p className="font-medium text-[#3D4F6F] line-clamp-2 text-sm">{listenedItem?.title}</p>
                    <p className="text-xs text-[#64748B] mt-1">New</p>
                  </button>
                  
                  {/* Existing Item */}
                  <button
                    onClick={() => handleComparisonChoice(false)}
                    className="p-4 rounded-xl border-2 border-slate-200 hover:border-[#F87171] hover:bg-[#F87171]/5 transition-all text-left"
                    data-testid="prefer-existing-btn"
                  >
                    <div className="aspect-square rounded-lg overflow-hidden bg-slate-100 mb-3">
                      {comparisonItem?.image ? (
                        <img src={comparisonItem.image} alt={comparisonItem.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center"><Mic className="w-10 h-10 text-slate-300" /></div>
                      )}
                    </div>
                    <p className="font-medium text-[#3D4F6F] line-clamp-2 text-sm">{comparisonItem?.title}</p>
                    <p className="text-xs text-[#64748B] mt-1">#{comparisonItem?.rank_in_group}</p>
                  </button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Rank New Podcast Dialog */}
      <Dialog open={isRankDialogOpen} onOpenChange={(open) => { if (!open) { setIsRankDialogOpen(false); setNewlyAddedPodcast(null); setRankingListId(null); } }}>
        <DialogContent className="max-w-lg max-h-[80vh]" data-testid="rank-podcast-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit text-[#3D4F6F]">Rank Your Priority</DialogTitle>
            <DialogDescription className="text-[#64748B]">
              Drag <span className="font-semibold text-[#F87171]">{newlyAddedPodcast?.title}</span> to set your listening priority.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 max-h-[400px] overflow-y-auto">
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleRankingDragEnd}>
              <SortableContext items={rankingOrder.map(p => p.podcast_id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-2">
                  {rankingOrder.map((podcast) => (
                    <SortableRankingItem 
                      key={podcast.podcast_id} 
                      podcast={podcast} 
                      isHighlighted={podcast.podcast_id === newlyAddedPodcast?.podcast_id}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => { setIsRankDialogOpen(false); setNewlyAddedPodcast(null); setRankingListId(null); toast.success(`Added "${newlyAddedPodcast?.title}"!`); }} className="rounded-full">
              Skip
            </Button>
            <Button onClick={handleSaveRankingOrder} className="bg-[#F87171] hover:bg-[#EF4444] text-white rounded-full" data-testid="save-rank-btn">
              <Check className="w-4 h-4 mr-2" />Save Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
